import { Delete } from '@mui/icons-material';
import { Box, Stack } from '@mui/material';
import { ROUTES } from 'config';
import {
  Button,
  DataLossPrompt,
  FileInput,
  FileLink,
  Footer,
  ImageList,
  MultiLanguageInputContainer,
  Panel,
  RichTextInput,
  SelectInput,
  SortableList,
  TextInput,
} from 'melp-design/components';
import { SystemColors } from 'melp-design/style';
import { FC, useEffect, useMemo } from 'react';
import {
  Control,
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { useCompanyLanguages, useMe } from 'state/Administrators';
import { useBenefitContent, useUpdateBenefitContent } from 'store/benefits';
import { useUploadFile } from 'store/files';
import { Translations } from 'types/Common';
import { ContentLanguage } from 'types/general';
import { formatLanguage } from 'utils/format';
import { resolveTranslation, stripHtml } from 'utils/general';
import { usePredefinedToasts } from 'utils/Toast';
import { useRouteParams } from 'utils/useIdParam';

interface Values {
  languages: Array<Uppercase<ContentLanguage>>;
  imageId: string;
  translations: Translations<{
    title: string;
    description: string;
    links: Array<{ name: string; url: string }>;
    faqs: Array<{ question: string; answer: string }>;
    attachments: Array<{ fileId: string; name: string }>;
  }>;
}

const getInitialValues = (
  defaultLanguage: Uppercase<ContentLanguage>,
): Values => ({
  languages: [defaultLanguage],
  imageId: '',
  translations: [
    {
      language: defaultLanguage,
      title: '',
      description: '',
      links: [],
      faqs: [],
      attachments: [],
    },
  ],
});

const INITIAL_LINK: Values['translations'][0]['links'][number] = {
  name: '',
  url: '',
};
const INITIAL_FAQ: Values['translations'][0]['faqs'][number] = {
  question: '',
  answer: '',
};

const AttachmentsList: FC<{
  control: Control<Values>;
  translationIndex: number;
}> = ({ control, translationIndex }) => {
  const { t } = useTranslation();
  const { canEditBenefits, hasEditPermission } = useMe();
  const canEdit = canEditBenefits && hasEditPermission(['benefits']);

  const {
    fields: attachmentFields,
    append: appendAttachment,
    move: moveAttachments,
    remove: removeAttachment,
  } = useFieldArray({
    control,
    name: `translations.${translationIndex}.attachments`,
  });

  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();

  return (
    <Controller
      name={`translations.${translationIndex}.attachments`}
      control={control}
      render={({ field: { name, value, onChange, ref } }) => (
        <Stack gap={1}>
          <FileInput
            name={name}
            onChange={([file]) => {
              if (file) {
                uploadFile(
                  { file },
                  {
                    onSuccess: ({ data }) => {
                      appendAttachment({ fileId: data.id, name: file.name });
                    },
                  },
                );
              } else {
                onChange(value);
              }
            }}
            disabled={isUploading || !canEdit}
            maxFiles={1}
            previewSelectedFiles={false}
            accept={[
              'jpg',
              'jpeg',
              'png',
              'gif',
              'webp',
              'txt',
              'pdf',
              'doc',
              'docx',
              'xls',
              'xlsx',
            ]}
            ref={ref}
          />

          <SortableList
            key={JSON.stringify(attachmentFields.map((field) => field.id))}
            onPositionChange={moveAttachments}
            disabled={!canEdit}
            items={attachmentFields.map((field, i) => ({
              id: field.id,
              content: (
                <Stack
                  direction="row"
                  alignItems="center"
                  py={0.5}
                  px={2}
                  spacing={2}
                  sx={{ backgroundColor: SystemColors.white }}
                  borderRadius={1}
                  boxShadow={`0 0 0 1px ${SystemColors.grey[90]}`}
                  key={field.id}
                >
                  <SortableList.DragIndicator />

                  <Box flex={1}>
                    <FileLink id={field.fileId} />
                  </Box>

                  <Box flexShrink={0}>
                    <Button
                      variant="neutral-outline"
                      size="md"
                      icon={Delete}
                      disabled={!canEdit}
                      onClick={() => removeAttachment(i)}
                      title={t('common.remove')}
                    />
                  </Box>
                </Stack>
              ),
            }))}
          />
        </Stack>
      )}
    />
  );
};

const LinksList: FC<{
  control: Control<Values>;
  translationIndex: number;
}> = ({ control, translationIndex }) => {
  const { t } = useTranslation();
  const { canEditBenefits, hasEditPermission } = useMe();
  const canEdit = canEditBenefits && hasEditPermission(['benefits']);

  const {
    fields: linksFields,
    append: appendLinks,
    move: moveLink,
    remove: removeLinks,
  } = useFieldArray({
    control,
    name: `translations.${translationIndex}.links`,
  });

  return (
    <Stack gap={1}>
      <SortableList
        key={JSON.stringify(linksFields.map((field) => field.id))}
        onPositionChange={moveLink}
        disabled={!canEdit}
        items={linksFields.map((field, i) => ({
          id: field.id,
          content: (
            <Stack
              direction="row"
              alignItems="center"
              p={1}
              spacing={2}
              sx={{ backgroundColor: SystemColors.white }}
              borderRadius={1}
              boxShadow={`0 0 0 1px ${SystemColors.grey[90]}`}
              key={field.id}
            >
              <SortableList.DragIndicator />

              <Stack flex={1} gap={1}>
                <Controller
                  name={`translations.${translationIndex}.links.${i}.name`}
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <TextInput
                      label={t('form.link_title')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />

                <Controller
                  name={`translations.${translationIndex}.links.${i}.url`}
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <TextInput
                      label={t('form.link_url')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />
              </Stack>

              <Box flexShrink={0}>
                <Button
                  variant="neutral-outline"
                  size="md"
                  icon={Delete}
                  disabled={!canEdit}
                  onClick={() => removeLinks(i)}
                  title={t('common.remove')}
                />
              </Box>
            </Stack>
          ),
        }))}
      />

      <Box>
        <Button
          label={t('buttons.add_link')}
          variant="tertiary"
          disabled={!canEdit}
          onClick={() => {
            appendLinks(INITIAL_LINK);
          }}
        />
      </Box>
    </Stack>
  );
};

const FaqList: FC<{
  control: Control<Values>;
  translationIndex: number;
}> = ({ control, translationIndex }) => {
  const { t } = useTranslation();
  const { canEditBenefits, hasEditPermission } = useMe();
  const canEdit = canEditBenefits && hasEditPermission(['benefits']);

  const {
    fields: faqFields,
    append: appendFaq,
    move: moveFaq,
    remove: removeFaq,
  } = useFieldArray({
    control,
    name: `translations.${translationIndex}.faqs`,
  });

  return (
    <Stack gap={1}>
      <SortableList
        key={JSON.stringify(faqFields.map((field) => field.id))}
        onPositionChange={moveFaq}
        disabled={!canEdit}
        items={faqFields.map((field, i) => ({
          id: field.id,
          content: (
            <Stack
              direction="row"
              alignItems="center"
              p={1}
              spacing={2}
              sx={{ backgroundColor: SystemColors.white }}
              borderRadius={1}
              boxShadow={`0 0 0 1px ${SystemColors.grey[90]}`}
              key={field.id}
            >
              <SortableList.DragIndicator />

              <Stack flex={1} gap={1}>
                <Controller
                  name={`translations.${translationIndex}.faqs.${i}.question`}
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <TextInput
                      label={t('surveys.question')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />

                <Controller
                  name={`translations.${translationIndex}.faqs.${i}.answer`}
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <TextInput
                      label={t('form.answer')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required
                      disabled={!canEdit}
                      rows={5}
                      ref={ref}
                    />
                  )}
                />
              </Stack>

              <Box flexShrink={0}>
                <Button
                  variant="neutral-outline"
                  size="md"
                  icon={Delete}
                  disabled={!canEdit}
                  onClick={() => removeFaq(i)}
                  title={t('common.remove')}
                />
              </Box>
            </Stack>
          ),
        }))}
      />

      <Box>
        <Button
          label={t('surveys.addQuestion')}
          variant="tertiary"
          disabled={!canEdit}
          onClick={() => {
            appendFaq(INITIAL_FAQ);
          }}
        />
      </Box>
    </Stack>
  );
};

interface Props {
  onSaveSuccess: () => void;
  guided: boolean;
}

export const BenefitContent: FC<Props> = ({ onSaveSuccess, guided }) => {
  const { t, i18n } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const { id: benefitId } = useRouteParams(ROUTES.benefits.details);
  const { defaultLanguage, supportedLanguages } = useCompanyLanguages();
  const { isFeatureEnabled, canEditBenefits, hasEditPermission } = useMe();
  const canEdit = canEditBenefits && hasEditPermission(['benefits']);

  const INITIAL_VALUES = useMemo(
    () => getInitialValues(defaultLanguage),
    [defaultLanguage],
  );

  const { data: benefit } = useBenefitContent(benefitId);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { dirtyFields, isSubmitted },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });
  const { fields: translationsFields, replace: replaceTranslations } =
    useFieldArray({ name: 'translations', control });

  useEffect(() => {
    if (benefit && benefit.translations.length) {
      reset({
        languages: benefit.translations.map((trans) => trans.language),
        imageId: benefit.translations[0].image?.id ?? '',
        translations: benefit.translations.map((trans) => ({
          language: trans.language,
          title: trans.title,
          description: trans.description,
          attachments: trans.attachments.map((attachment) => ({
            fileId: attachment.file?.id || '',
            name: attachment.name,
            index: attachment.index,
          })),
          links: trans.links.map((link) => ({
            name: link.name,
            url: link.url,
            index: link.index,
          })),
          faqs: trans.faqs.map((faq) => ({
            question: faq.question,
            answer: faq.answer,
            index: faq.index,
          })),
        })),
      });
    }
  }, [benefit, reset]);

  const { mutate: updateBenefit, isLoading: isUpdating } =
    useUpdateBenefitContent();
  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateBenefit(
      {
        benefitId,
        data: {
          translations: values.translations.map((trans) => ({
            ...trans,
            imageId: values.imageId || null,
            links: trans.links.map((link, i) => ({
              ...link,
              index: i,
            })),
            faqs: trans.faqs.map((faq, i) => ({
              ...faq,
              index: i,
            })),
            attachments: trans.attachments.map((attachment, i) => ({
              ...attachment,
              fileId: attachment.fileId || null,
              index: i,
            })),
          })),
        },
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
          onSaveSuccess();
        },
      },
    );
  };

  const [languages] = watch(['languages']);
  const isDirty = !!Object.keys(dirtyFields).length && !isSubmitted;
  const translation = resolveTranslation(benefit?.translations ?? []);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Panel title={t('benefitTemplates.templateContent')}>
            <Stack spacing={2}>
              <Controller
                name="languages"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <SelectInput
                    label={t('companies.supported_languages')}
                    name={name}
                    value={value}
                    onChange={(value) => {
                      const selectedLanguages = value as Array<
                        Uppercase<ContentLanguage>
                      >;
                      replaceTranslations(
                        selectedLanguages.map(
                          (language) =>
                            getValues('translations').find(
                              (t) => t.language === language,
                            ) ?? {
                              ...INITIAL_VALUES.translations[0],
                              language,
                            },
                        ),
                      );
                      onChange(selectedLanguages);
                    }}
                    options={supportedLanguages.map((language) => ({
                      value: language,
                      label: formatLanguage(i18n.language, language),
                      disabled:
                        language === defaultLanguage &&
                        value.includes(defaultLanguage), // PDB-69 (https://www.notion.so/2a0916503d46808ca6d9ef64fe3d3810?v=2a0916503d46806ab1c5000c7289875f&p=2f0916503d4680b6b25cf14d6782510a&pm=s)
                    }))}
                    multiple
                    disabled={!canEdit}
                    required
                    ref={ref}
                  />
                )}
              />

              {languages.length ? (
                <>
                  <MultiLanguageInputContainer
                    label={t('benefits.title')}
                    required
                    fields={translationsFields.map((field, i) => ({
                      id: field.id,
                      language: field.language,
                      component: (
                        <Controller
                          name={`translations.${i}.title`}
                          control={control}
                          render={({
                            field: { name, value, onChange, ref },
                          }) => (
                            <TextInput
                              name={name}
                              value={value}
                              onChange={onChange}
                              required
                              disabled={!canEdit}
                              ai={
                                isFeatureEnabled('ai')
                                  ? {
                                      inputLanguage: defaultLanguage,
                                      outputLanguage: field.language,
                                      text:
                                        getValues('translations').find(
                                          (t) => t.language === defaultLanguage,
                                        )?.title ?? '',
                                    }
                                  : undefined
                              }
                              ref={ref}
                            />
                          )}
                        />
                      ),
                    }))}
                  />

                  <MultiLanguageInputContainer
                    label={t('employees.details')}
                    required
                    fields={translationsFields.map((field, i) => ({
                      id: field.id,
                      language: field.language,
                      component: (
                        <Controller
                          name={`translations.${i}.description`}
                          control={control}
                          render={({ field: { value, onChange, ref } }) => (
                            <RichTextInput
                              value={value}
                              onChange={onChange}
                              minHeight={250}
                              maxHeight={500}
                              disabled={!canEdit}
                              ai={
                                isFeatureEnabled('ai')
                                  ? {
                                      inputLanguage: defaultLanguage,
                                      outputLanguage: field.language,
                                      text:
                                        getValues('translations').find(
                                          (t) => t.language === defaultLanguage,
                                        )?.description ?? '',
                                    }
                                  : undefined
                              }
                              ref={ref}
                            />
                          )}
                        />
                      ),
                    }))}
                  />
                </>
              ) : null}

              <Controller
                name="imageId"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return value ? (
                    <ImageList
                      items={[
                        {
                          imageId: value,
                          actions: canEdit
                            ? [
                                {
                                  icon: Delete,
                                  variant: 'neutral-outline',
                                  onClick: () => onChange(''),
                                },
                              ]
                            : [],
                        },
                      ]}
                    />
                  ) : (
                    <FileInput
                      name={name}
                      label={t('form.image')}
                      onChange={([file]) => {
                        if (file) {
                          uploadFile(
                            { file },
                            {
                              onSuccess: ({ data }) => {
                                onChange(data.id);
                              },
                            },
                          );
                        } else {
                          onChange('');
                        }
                      }}
                      disabled={isUploading || !canEdit}
                      maxFiles={1}
                      previewSelectedFiles={false}
                      accept={['jpg', 'jpeg', 'png', 'gif', 'webp']}
                      ai={
                        isFeatureEnabled('ai')
                          ? {
                              title: translation?.title,
                              description: translation?.description
                                ? stripHtml(translation.description)
                                : undefined,
                              aspectRatio: [16, 9],
                            }
                          : undefined
                      }
                      ref={ref}
                    />
                  );
                }}
              />
            </Stack>
          </Panel>

          <Panel title={t('form.attachments')}>
            <Stack spacing={2}>
              <MultiLanguageInputContainer
                fields={translationsFields.map((field, i) => ({
                  id: field.id,
                  language: field.language,
                  component: (
                    <AttachmentsList control={control} translationIndex={i} />
                  ),
                }))}
              />
            </Stack>
          </Panel>

          <Panel title={t('benefits.pdfLinks')}>
            <Stack spacing={2}>
              <MultiLanguageInputContainer
                fields={translationsFields.map((field, i) => ({
                  id: field.id,
                  language: field.language,
                  component: (
                    <LinksList control={control} translationIndex={i} />
                  ),
                }))}
              />
            </Stack>
          </Panel>

          <Panel title={t('form.faq')}>
            <Stack spacing={2}>
              <MultiLanguageInputContainer
                fields={translationsFields.map((field, i) => ({
                  id: field.id,
                  language: field.language,
                  component: <FaqList control={control} translationIndex={i} />,
                }))}
              />
            </Stack>
          </Panel>
        </Stack>

        <Footer visible={isDirty || guided}>
          <Stack direction="row" justifyContent="end" gap={1}>
            <Button
              label={t('common.cancel')}
              variant="neutral-outline"
              onClick={() => {
                reset(undefined, { keepDirty: false });
              }}
            />
            <Button
              label={t('common.save')}
              variant="primary"
              type="submit"
              disabled={
                isUpdating ||
                isUploading ||
                !canEdit ||
                !hasEditPermission(['benefits'])
              }
            />
          </Stack>
        </Footer>
      </form>

      <DataLossPrompt when={isDirty && hasEditPermission(['benefits'])} />
    </>
  );
};
